// Persistent "Saved Ad Copy" strip — shows the headlines/descriptions the
// user pinned from the Rival Ad-Copy Snoop tool (variations included).
// Rendered above the Keyword Planner and inside the Campaign Push Wizard.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Copy,
    Sparkle,
    Trash,
    CaretDown,
    CaretRight,
} from "@phosphor-icons/react";
import {
    readAdClipboard,
    subscribeAdClipboard,
    removeAdClipboard,
    clearAdClipboard,
} from "@/lib/adClipboard";

const ANGLE_CLS = {
    urgency: "bg-red-100 text-red-900 border-red-300",
    trust: "bg-emerald-100 text-emerald-900 border-emerald-300",
    benefit: "bg-klein/10 text-klein border-klein/30",
};

export const AdClipboardStrip = ({ defaultOpen = false } = {}) => {
    const [items, setItems] = useState(() => readAdClipboard());
    const [open, setOpen] = useState(defaultOpen);

    useEffect(() => subscribeAdClipboard(setItems), []);

    if (!items.length && !open) {
        return null;
    }

    const headlines = items.filter((i) => i.kind === "headline");
    const descriptions = items.filter((i) => i.kind === "description");

    const copyOne = async (text) => {
        try {
            await navigator.clipboard?.writeText(text);
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Clipboard blocked — select and copy manually");
        }
    };

    const copyAll = async (kind) => {
        const list = kind === "headline" ? headlines : descriptions;
        if (!list.length) return;
        try {
            await navigator.clipboard?.writeText(list.map((i) => i.text).join("\n"));
            toast.success(`Copied ${list.length} ${kind}${list.length === 1 ? "" : "s"}`);
        } catch {
            toast.error("Clipboard blocked");
        }
    };

    return (
        <div
            data-testid="ad-clipboard-strip"
            className="border-2 border-ink bg-yellow-50"
        >
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="ad-clipboard-toggle"
                className="w-full px-4 py-2 flex items-center gap-2 text-left"
            >
                <Sparkle size={13} weight="bold" className="text-klein" />
                <span className="mono-label text-ink">Saved Ad Copy</span>
                <span
                    data-testid="ad-clipboard-count"
                    className="mono-label bg-ink text-white px-1.5 py-0.5"
                >
                    {items.length}
                </span>
                <span className="text-[10px] font-mono text-neutral-500">
                    {headlines.length} headlines · {descriptions.length} descriptions
                </span>
                <div className="flex-1" />
                {open ? (
                    <CaretDown size={12} weight="bold" className="text-neutral-500" />
                ) : (
                    <CaretRight size={12} weight="bold" className="text-neutral-500" />
                )}
            </button>
            {open && (
                <div className="border-t border-ink/20 p-3 space-y-3 max-h-[280px] overflow-y-auto thin-scroll">
                    {!items.length && (
                        <div className="text-[11px] font-mono text-neutral-500">
                            Pin headlines and descriptions from the Auction Insights → Snoop
                            tool. Generated variations appear here so you can drop them
                            straight into a campaign.
                        </div>
                    )}
                    {headlines.length > 0 && (
                        <Section
                            title="Headlines"
                            limit={30}
                            items={headlines}
                            onCopy={copyOne}
                            onCopyAll={() => copyAll("headline")}
                        />
                    )}
                    {descriptions.length > 0 && (
                        <Section
                            title="Descriptions"
                            limit={90}
                            items={descriptions}
                            onCopy={copyOne}
                            onCopyAll={() => copyAll("description")}
                        />
                    )}
                    {items.length > 0 && (
                        <div className="pt-1 flex justify-end">
                            <button
                                data-testid="ad-clipboard-clear"
                                onClick={() => {
                                    clearAdClipboard();
                                    toast.success("Ad clipboard cleared");
                                }}
                                className="h-6 px-2 border border-neutral-400 text-neutral-700 hover:border-red-500 hover:text-red-700 font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1"
                            >
                                <Trash size={10} weight="bold" /> Clear all
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const Section = ({ title, items, limit, onCopy, onCopyAll }) => (
    <div>
        <div className="flex items-center gap-2 mb-1">
            <span className="mono-label">{title}</span>
            <span className="text-[10px] font-mono text-neutral-400">≤ {limit} chars</span>
            <div className="flex-1" />
            <button
                data-testid={`ad-clipboard-copyall-${title.toLowerCase()}`}
                onClick={onCopyAll}
                className="h-5 px-1.5 border border-ink bg-white hover:bg-ink hover:text-white font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1"
            >
                <Copy size={9} weight="bold" /> Copy all
            </button>
        </div>
        <div className="space-y-1">
            {items.map((it) => {
                const cls = it.angle ? ANGLE_CLS[it.angle] : "bg-white border-neutral-300";
                return (
                    <div
                        key={it.id}
                        data-testid={`ad-clipboard-item-${it.id}`}
                        className={`border ${cls} p-1.5 flex items-center gap-2`}
                    >
                        {it.angle && (
                            <span className="px-1 py-0.5 text-[8.5px] font-mono uppercase tracking-widest font-bold bg-white/70 border border-current shrink-0">
                                {it.angle}
                            </span>
                        )}
                        <div className="flex-1 min-w-0 text-xs font-mono truncate">
                            {it.text}
                        </div>
                        <span className="text-[9px] font-mono text-neutral-500 shrink-0">
                            {it.text.length}
                        </span>
                        {it.source_domain && (
                            <span className="text-[9px] font-mono text-neutral-500 shrink-0 truncate max-w-[120px]">
                                · {it.source_domain}
                            </span>
                        )}
                        <button
                            onClick={() => onCopy(it.text)}
                            className="h-5 px-1.5 border border-ink bg-white hover:bg-ink hover:text-white font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1 shrink-0"
                            title="Copy this line"
                        >
                            <Copy size={9} weight="bold" />
                        </button>
                        <button
                            onClick={() => removeAdClipboard(it.id)}
                            className="h-5 w-5 border border-neutral-300 hover:border-red-500 hover:text-red-700 text-neutral-500 flex items-center justify-center shrink-0"
                            title="Remove"
                        >
                            <Trash size={9} weight="bold" />
                        </button>
                    </div>
                );
            })}
        </div>
    </div>
);
